import React, { useState, useEffect, useRef } from 'react';
import ReactGA from 'react-ga4';
import { useScroll } from '../utils/scroll-utils';

import Home from './Home';
import About from './About';
import Paiste from './Paiste';
import Collision from './Collision';
import Press from './Press';
import Shows from './Shows';
import Footer from './Footer';

import '../styles/App.scss';

import config from '../config/web-site.json';

const videoAsset = require(`../assets/video/${config['background-video']}`);
const IOS_SAFARI_SCROLL_CLASS = 'ios-safari-scroll';

const App: React.FC = () => {
  const [, setScrollingManually] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);
  const [hideBackPicture, setBackPicture] = useState('hidden');
  const [aboutExpanded, setAboutExpanded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<HTMLDivElement[]>([]);
  const isFirstRender = useRef(true);
  const useDocumentScroll = document.documentElement.classList.contains(IOS_SAFARI_SCROLL_CLASS);

  const onComponentVisible = (componentName: string, direction: string) => {
    let message = `${componentName} viewed`;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      message += ' first render';
    } else {
      message += ` scrolling ${direction}`;
    }
    ReactGA.event({
      category: config.analytics.parameters.categories.navigation,
      action: message,
      label: config.analytics.parameters.labels.scroll,
    });
  };

  const {
    handleVisibilityChange,
    scrollToPage,
    handleScroll,
  } = useScroll(containerRef, sectionRefs, onComponentVisible, setScrollingManually, hasMounted, useDocumentScroll);

  const handleScrollAndTouchMove = () => {
    handleScroll();

    if (useDocumentScroll) {
      setBackPicture('');
      return;
    }

    const buffer = 750;
    const scrollTop = containerRef.current?.scrollTop;
    const clientHeight = containerRef.current?.clientHeight;
    const scrollHeight = containerRef.current?.scrollHeight;

    if (scrollTop == null || clientHeight == null || scrollHeight == null) return;

    if (scrollTop <= 0 || scrollTop + clientHeight >= scrollHeight - buffer) {
      setBackPicture('hidden');
    } else {
      setBackPicture('');
    }
  };

  const handleCarouselSlideChange = () => {
    const carousel = document.querySelector('.carousel');
    if (carousel) {
      carousel.addEventListener('slide.bs.carousel', () => {
        const activeSlide = carousel.querySelector('.carousel-item.active');
        if (activeSlide) {
          const slideIndex = Array.from(carousel.children).indexOf(activeSlide);
          onComponentVisible(`Slide ${slideIndex}`, 'slide change');
        }
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleVisibilityChange, {
      root: useDocumentScroll ? null : containerRef.current,
      threshold: 0.5,
    });

    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => observer.disconnect();
  }, [handleVisibilityChange, useDocumentScroll]);

  useEffect(() => {
    setHasMounted(true);
    const container = containerRef.current;
    const scrollEventTarget = useDocumentScroll ? window : container;

    scrollEventTarget?.addEventListener('scroll', handleScrollAndTouchMove, { passive: true });
    scrollEventTarget?.addEventListener('touchmove', handleScrollAndTouchMove, { passive: true });

    handleCarouselSlideChange();

    return () => {
      scrollEventTarget?.removeEventListener('scroll', handleScrollAndTouchMove);
      scrollEventTarget?.removeEventListener('touchmove', handleScrollAndTouchMove);
    };
  }, [handleScroll, useDocumentScroll]);

  useEffect(() => {
      const pageId = location.pathname.replace('/', '');
      if(sections.some((section) => section.id === pageId)) {
        if (pageId === 'about') {
          scrollToPage(pageId);
          setAboutExpanded(true);
        }
      }
    }, [location]);

  ReactGA.initialize(
    config.analytics['tracking-id'], {
      testMode: process.env.NODE_ENV != 'production',
    }
  );

  const sections = [
    { id: 'home', component: <Home scroller={scrollToPage} /> },
    { id: 'about', component: <About scroller={scrollToPage} expanded={aboutExpanded} setExpanded={setAboutExpanded} /> },
    { id: 'press', component: <Press scroller={scrollToPage} /> },
    { id: 'picture-window', component: <section data-testid='picture-window-view' /> },
    { id: 'shows', component: <Shows scroller={scrollToPage} /> },
     { id: 'paiste', component: <Paiste scroller={scrollToPage} /> },
    { id: 'collision', component: <Collision scroller={scrollToPage} /> },
    { id: 'footer', component: <Footer /> },
  ];

  return (
    <main ref={containerRef}>
      <section id='main-section' data-testid='app-view'>
        <section id='picture' data-testid='picture-view' className={hideBackPicture} >
          <video
            className="picture-background-video"
            src={videoAsset}
            autoPlay
            loop
            muted
            playsInline
          />
        </section>
        {sections.map((section, index) => (
          <div
            key={section.id}
            id={section.id}
            ref={(el) => {
              if (el) sectionRefs.current[index] = el;
            }}
          >
            {section.component}
          </div>
        ))}
      </section>
    </main>
  );
};

export default App;
